import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  Typography
} from '@material-ui/core'
import { Form, Formik } from 'formik'
import PropType from 'prop-types'
import { useContext, useEffect, useRef, useState } from 'react'
import * as Yup from 'yup'
import { AuthContext } from '../../context/Auth/AuthContext'
import { i18n } from '../../translate/i18n'
import { InputField } from '../InputField'

import { useStyles } from './style'

import { get, has, isNil, isObject } from 'lodash'
import { toast } from 'react-toastify'
import toastError from '../../errors/toastError'
import useQuickMessages from '../../hooks/useQuickMessages'
import { AttachmentInput } from '../AttachmentInput'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { QueueDepartmentSelect } from '../QueueDepartmentSelect'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20 MB

const MessageSchema = Yup.object().shape({
  shortcode: Yup.string()
    .min(3, 'Limite mínimo de 3 caracteres')
    .max(20, 'Limite máximo de 20 caracteres')
    .required('Obrigatório'),
  message: Yup.string()
    .min(3, 'Limite mínimo de 3 caracteres')
    .required('Obrigatório'),
  visibility: Yup.string()
    .oneOf(['all', 'me', 'department'])
    .required('Obrigatório'),
  queueIds: Yup.array().when('visibility', {
    is: 'department',
    then: schema => schema.min(1, 'Selecione ao menos um departamento'),
    otherwise: schema => schema
  })
})

function QuickMessageDialog(props) {
  const classes = useStyles()

  const initialMessage = {
    id: null,
    shortcode: '',
    message: '',
    visibility: 'all',
    queueIds: []
  }

  const { modalOpen, saveMessage, editMessage, onClose, messageSelected } =
    props
  const [dialogOpen, setDialogOpen] = useState(false)
  const [message, setMessage] = useState(initialMessage)
  const [loading, setLoading] = useState(false)
  const [attachment, setAttachment] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [deleteExistingMedia, setDeleteExistingMedia] = useState(false)
  const attachmentRef = useRef(null)

  const { user } = useContext(AuthContext)
  const { mediaUpload, deleteMedia } = useQuickMessages()
  const userQueues = (Array.isArray(user?.queues) ? user.queues : []).filter(
    queue =>
      queue.notificationact !== true &&
      queue.UserQueue?.notificationact !== true
  )

  useEffect(() => {
    verifyAndSetMessage()
    setDialogOpen(modalOpen)
    if (!modalOpen) {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl)
      }
      setAttachment(null)
      setPreviewUrl(null)
      setDeleteExistingMedia(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen])

  useEffect(() => {
    verifyAndSetMessage()
    setAttachment(null)
    setPreviewUrl(null)
    setDeleteExistingMedia(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageSelected])

  const messageSelectedIsValid = () => {
    return (
      isObject(messageSelected) &&
      has(messageSelected, 'id') &&
      !isNil(get(messageSelected, 'id'))
    )
  }

  const verifyAndSetMessage = () => {
    if (messageSelectedIsValid()) {
      const { id, message, shortcode, queues, userId } = messageSelected
      const queueIds = Array.isArray(queues)
        ? queues.map(queue => Number(queue.id)).filter(Boolean)
        : []
      const visibility =
        queueIds.length > 0 ? 'department' : userId ? 'me' : 'all'
      setMessage({ id, message, shortcode, visibility, queueIds })
    } else {
      setMessage(initialMessage)
    }
  }

  const handleClose = () => {
    onClose()
    setLoading(false)
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl)
    }
    setAttachment(null)
    setPreviewUrl(null)
    setDeleteExistingMedia(false)
  }

  const handleAttachmentChange = e => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > MAX_FILE_SIZE) {
      toast.error('O arquivo deve ter no máximo 20 MB.')
      e.target.value = null
      return
    }
    setAttachment(file)
    setDeleteExistingMedia(false)
  }

  const handleRemoveAttachment = () => {
    setAttachment(null)
    if (attachmentRef.current) attachmentRef.current.value = null
    if (messageSelectedIsValid() && messageSelected.mediaPath) {
      setDeleteExistingMedia(true)
    }
  }

  const currentMediaName =
    attachment?.name ||
    (!deleteExistingMedia && messageSelected?.mediaName) ||
    null

  const handleSave = async values => {
    setLoading(true)
    try {
      let record
      if (messageSelectedIsValid()) {
        record = await editMessage({
          ...messageSelected,
          ...values,
          userId: user.id
        })
      } else {
        record = await saveMessage({
          ...values,
          userId: user.id
        })
      }

      const recordId = record?.id ?? messageSelected?.id

      if (recordId) {
        if (deleteExistingMedia) {
          await deleteMedia(recordId)
        }
        if (attachment) {
          const formData = new FormData()
          formData.append('file', attachment)
          await mediaUpload(recordId, formData)
        }
      }

      handleClose()
    } catch (err) {
      toastError(err)
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleClose}
      className={classes.root}
      maxWidth="xs"
      fullWidth
      scroll="paper"
    >
      <DialogTitle className={classes.dialogTitle}>
        Respostas Rápidas
      </DialogTitle>
      <Formik
        initialValues={message}
        enableReinitialize={true}
        validationSchema={MessageSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSave(values)
            actions.setSubmitting(false)
          }, 400)
        }}
      >
        {({ touched, errors, values, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <div className={classes.multField}>
                    <InputField
                      name="shortcode"
                      label={i18n.t('quickMessages.dialog.shortcode')}
                      error={touched.shortcode && Boolean(errors.shortcode)}
                      helperText={touched.shortcode && errors.shortcode}
                      inputProps={{ maxLength: 20 }}
                      placeholder="Digite um atalho com até 20 caracteres"
                    />
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <InputField
                    name="message"
                    minRows={6}
                    label={i18n.t('quickMessages.dialog.message')}
                    multiline={true}
                    spellCheck={true}
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                    variant="outlined"
                    emoji
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">
                      Essa mensagem está ativa para
                    </FormLabel>
                    <div className={classes.optionsContainer}>
                      <FormControlLabel
                        control={
                          <Radio
                            color="primary"
                            checked={values.visibility === 'all'}
                            onChange={() => {
                              setFieldValue('visibility', 'all')
                              setFieldValue('queueIds', [])
                            }}
                            className={classes.radio}
                          />
                        }
                        label="Todos"
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            color="primary"
                            checked={values.visibility === 'me'}
                            onChange={() => {
                              setFieldValue('visibility', 'me')
                              setFieldValue('queueIds', [])
                            }}
                            className={classes.radio}
                          />
                        }
                        label="Apenas pra mim"
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            color="primary"
                            checked={values.visibility === 'department'}
                            onChange={() =>
                              setFieldValue('visibility', 'department')
                            }
                            className={classes.radio}
                          />
                        }
                        label="Departamento"
                      />
                    </div>
                  </FormControl>
                </Grid>
                {values.visibility === 'department' ? (
                  <Grid item xs={12}>
                    <QueueDepartmentSelect
                      label="Departamentos Vinculados"
                      includeAllOption={true}
                      showBots={false}
                      filterByIsBot={true}
                      isMulti={true}
                      disabled={loading}
                      customOptions={userQueues}
                      value={userQueues.filter(queue =>
                        (values.queueIds || []).includes(Number(queue.id))
                      )}
                      onChange={selectedDepartments => {
                        const ids = selectedDepartments
                          .map(d => Number(d.id))
                          .filter(Boolean)
                        setFieldValue('queueIds', ids)
                      }}
                    />

                    {userQueues.length === 0 && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: 'block', marginTop: 4 }}
                      >
                        Nenhum departamento vinculado ao seu usuário.
                      </Typography>
                    )}

                    {touched.queueIds && errors.queueIds && (
                      <Typography
                        variant="caption"
                        color="error"
                        style={{ display: 'block', marginTop: 4 }}
                      >
                        {errors.queueIds}
                      </Typography>
                    )}
                  </Grid>
                ) : null}
                {/* <Grid item xs={12}>
                  <input
                    type="file"
                    ref={attachmentRef}
                    style={{ display: 'none' }}
                    onChange={handleAttachmentChange}
                  />
                  {currentMediaName ? (
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      <Paperclip size={16} />
                      <Typography
                        variant="body2"
                        style={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {currentMediaName}
                      </Typography>
                      <Tooltip title="Remover anexo">
                        <IconButton
                          size="small"
                          onClick={handleRemoveAttachment}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  ) : (
                    <Tooltip title="Anexar arquivo (máx. 20 MB)">
                      <ButtonPrimary
                        size="small"
                        onClick={() => attachmentRef.current?.click()}
                        label="Anexar"
                        startIcon={<Paperclip size={18} />}
                      />
                    </Tooltip>
                  )}
                </Grid> */}

                <Grid item xs={12} style={{ marginTop: '8px' }}>
                  <AttachmentInput
                    type="image"
                    variant="body2"
                    component="span"
                    mediaPath={
                      previewUrl ||
                      (!deleteExistingMedia && messageSelected?.mediaPath) ||
                      null
                    }
                    mediaName={
                      attachment?.name ||
                      (!deleteExistingMedia && messageSelected?.mediaName) ||
                      null
                    }
                    displayName="resposta rápida"
                    onFileSelect={(file, url) => {
                      setAttachment(file)
                      setPreviewUrl(url)
                      setDeleteExistingMedia(false)
                    }}
                    onFileRemove={() => {
                      setAttachment(null)
                      setPreviewUrl(null)
                      if (
                        messageSelectedIsValid() &&
                        messageSelected.mediaPath
                      ) {
                        setDeleteExistingMedia(true)
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <ButtonSecondary onClick={handleClose} label="Cancelar" />
              <ButtonPrimary
                label=" Salvar"
                loading={loading}
                color="primary"
                type="submit"
                variant="contained"
                autoFocus
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

QuickMessageDialog.propType = {
  modalOpen: PropType.bool,
  onClose: PropType.func
}

export default QuickMessageDialog
